import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Menu as MenuIcon,
  X,
  Instagram,
  MapPin,
  Utensils
} from 'lucide-react';
import { LanguageProvider, useLanguage } from './context/LanguageContext';
import { COLORS, LOGOS, BOOKING_URL, GOOGLE_MAPS_LINK } from './config';

// Pages
import Home from './pages/Home';
import ChiSiamo from './pages/ChiSiamo';
import Menu from './pages/Menu';
import DoveSiamo from './pages/DoveSiamo';
import News from './pages/News';

import './App.css';

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// Header Component
const Header = () => {
  const { language, setLanguage, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '/chi-siamo', label: t('nav.chiSiamo') },
    { href: '/menu', label: t('nav.menu') },
    { href: '/dove-siamo', label: t('nav.doveSiamo') },
    { href: '/news', label: 'News' },
  ];

  const headerBg = isScrolled || !isHomePage;

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          headerBg ? 'glass-header shadow-lg' : 'bg-transparent'
        }`}
        data-testid="nav-menu"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-24 md:h-28">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img 
                src={headerBg ? LOGOS.nero : LOGOS.vanilla} 
                alt="Lola la Libertà" 
                className="h-16 md:h-20 w-auto transition-all duration-300"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-6">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`text-sm font-medium underline-draw transition-colors ${
                    headerBg 
                      ? 'text-[#722F37] hover:text-[#722F37]/80' 
                      : 'text-[#F5EDE0] hover:text-[#F5EDE0]/80'
                  } ${location.pathname === link.href ? 'font-bold' : ''}`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Right section */}
            <div className="flex items-center gap-4">
              {/* Price badge */}
              <div className="hidden sm:flex items-center gap-1 bg-[#D4A017] text-[#722F37] px-4 py-2 rounded-full">
                <span className="text-lg font-semibold">{language === 'it' ? '2,20\u20AC' : '2.20\u20AC'}</span>
                <span className="text-xs">{t('hero.priceLabel')}</span>
              </div>

              {/* Language Toggle */}
              <div className="lang-toggle" data-testid="lang-toggle">
                <button
                  onClick={() => setLanguage('it')}
                  className={`lang-btn ${language === 'it' ? 'active' : ''}`}
                >
                  IT
                </button>
                <button
                  onClick={() => setLanguage('en')}
                  className={`lang-btn ${language === 'en' ? 'active' : ''}`}
                >
                  EN
                </button>
              </div>

              {/* Mobile menu button */}
              <button
                className={`lg:hidden p-2 ${headerBg ? 'text-[#722F37]' : 'text-[#F5EDE0]'}`}
                onClick={() => setIsMobileMenuOpen(true)}
                aria-label="Open menu"
              >
                <MenuIcon size={24} />
              </button>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
            className="fixed inset-0 z-[100]"
            style={{ backgroundColor: COLORS.vaniglia }}
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-12">
                <img src={LOGOS.nero} alt="Lola" className="h-20 w-auto" />
                <button
                  className="p-2 text-[#722F37]"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <X size={24} />
                </button>
              </div>
              <nav className="flex flex-col gap-6">
                {navLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="text-[#722F37] text-2xl font-['Cormorant_Garamond'] font-semibold"
                  >
                    {link.label}
                  </Link>
                ))}
              </nav>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// Footer
const Footer = () => {
  const { t } = useLanguage();

  return (
    <footer className="py-16" style={{ backgroundColor: COLORS.vaniglia, borderTop: `1px solid ${COLORS.redwine}15` }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div>
            <img src={LOGOS.nero} alt="Lola la Libertà" className="h-20 w-auto mb-4" />
            <p className="text-sm italic mb-4" style={{ color: `${COLORS.redwine}99` }}>{t('footer.tagline')}</p>
            <p className="text-sm" style={{ color: `${COLORS.nero}99` }}>{t('footer.description')}</p>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="font-semibold mb-4" style={{ color: COLORS.redwine }}>{t('footer.navigation')}</h4>
            <div className="space-y-2">
              {[
                { to: '/chi-siamo', label: t('nav.chiSiamo') },
                { to: '/menu', label: t('nav.menu') },
                { to: '/dove-siamo', label: t('nav.doveSiamo') },
                { to: '/news', label: 'News' },
              ].map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="block text-sm transition-colors hover:opacity-80"
                  style={{ color: `${COLORS.nero}99` }}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold mb-4" style={{ color: COLORS.redwine }}>{t('footer.contact')}</h4>
            <div className="space-y-3">
              <a 
                href={GOOGLE_MAPS_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm group transition-colors hover:opacity-80"
                style={{ color: `${COLORS.nero}99` }}
              >
                <MapPin size={16} className="group-hover:scale-110 transition-transform" />
                {t('location.address')}
              </a>
            </div>
          </div>

          {/* Social */}
          <div>
            <h4 className="font-semibold mb-4" style={{ color: COLORS.redwine }}>{t('footer.social')}</h4>
            <div className="flex gap-4">
              <a
                href="https://www.instagram.com/lolalaliberta/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full flex items-center justify-center hover:scale-110 transition-all"
                style={{ backgroundColor: `${COLORS.redwine}15`, color: COLORS.redwine }}
                data-testid="social-instagram"
              >
                <Instagram size={18} />
              </a>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 text-center" style={{ borderTop: `1px solid ${COLORS.redwine}15` }}>
          <p className="text-sm" style={{ color: `${COLORS.nero}66` }}>
            © {new Date().getFullYear()} Lola la Libertà. {t('footer.rights')}
          </p>
          {/* Designer signature — minimal, discreet */}
          <p className="footer-credit" data-testid="footer-credit">
            Design &amp; Sviluppo:{' '}
            <a
              href="https://maglidesign.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="footer-credit-link"
            >
              Matteo Magli Digital Design
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

// Sticky Booking Button
const StickyBookingButton = () => {
  const { t } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.8, y: 20 }}
          className="sticky-booking"
          data-testid="sticky-booking-btn"
        >
          <a
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-6 py-4 rounded-full font-semibold shadow-lg hover:shadow-xl hover:brightness-110 transition-all pulse-glow wiggle-hover ripple-btn"
            style={{ backgroundColor: COLORS.gold, color: COLORS.redwine }}
          >
            <Utensils size={20} />
            {t('booking.button')}
          </a>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Main App Component
const AppContent = () => {
  return (
    <div className="min-h-screen">
      <ScrollToTop />
      <Header />
      <main className="pt-24 md:pt-28">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/chi-siamo" element={<ChiSiamo />} />
          <Route path="/menu" element={<Menu />} />
          <Route path="/dove-siamo" element={<DoveSiamo />} />
          <Route path="/news" element={<News />} />
          <Route path="/come-funziona" element={<Navigate to="/" replace />} />
          <Route path="/prezzo" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <Footer />
      <StickyBookingButton />
    </div>
  );
};

function App() {
  return (
    <Router>
      <LanguageProvider>
        <AppContent />
      </LanguageProvider>
    </Router>
  );
}

export default App;
